/**
 * /admin/v1/trace API 客户端
 *
 * 承接：T11 §3.1 admin/v1/trace · §4.2 M01 TraceDetail 数据源
 * 类型来源：S2 接 packages/api-types；本 PR 暂手写最小集对齐 T8 §3.1
 */
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export interface TraceVersionSnapshot {
	l1_prompt_version: string | null;
	l2_rule_version: string | null;
	playbook_version: string | null;
	skill_registry_version: string | null;
	judge_prompt_version: string | null;
	rag_index_version: string | null;
	customer_memory_version: string | null;
	api_version: string;
	data_mode: string | null;
	runtime_capabilities: TraceRuntimeCapabilities | null;
}

export interface TraceRuntimeCapabilities {
	phase4_runner_mode: string | null;
	prompt_effect_mode: string | null;
	rag_mode: string | null;
	session_mode: string | null;
	quality_mode: string | null;
	handoff_mode: string | null;
	shopify_read_mode: string | null;
	shopify_write_mode: string | null;
}

export interface IntentRecognitionEvent {
	phase: "intent_recognition";
	intent: string;
	service_stage: string;
	confidence: number;
	topic: string | null;
	implementation_level: "L1" | "L2" | "fused";
	l2_matched_rule_id: string | null;
	secondary_intent: string | null;
	intent_continuity: string;
}

export interface PlaybookRecommendationEvent {
	phase: "playbook_recommendation";
	playbook_id: string | null;
	playbook_name: string | null;
	playbook_version: string | null;
	selected_branch?: string | null;
	selected_branch_display_name?: string | null;
	selected_branch_source?: string | null;
	short_circuit_skill?: string | null;
	routing_rule_id?: string | null;
	recommended_skills: string[];
	universal_skills: string[];
	match_mode: string;
}

export type TraceEvent =
	| IntentRecognitionEvent
	| PlaybookRecommendationEvent
	| Record<string, unknown>;

export interface TraceDetail {
	trace_id: string;
	session_id: string;
	merchant_id: string;
	customer_id: string | null;
	turn_index: number;
	timestamp: string;
	total_latency_ms: number;
	final_action_type: string;
	final_outcome: string;
	degraded_modules: string[];
	handoff_reason: string | null;
	reply_format: string;
	estimated_token_count: number;
	version_snapshot: TraceVersionSnapshot;
	events: TraceEvent[];
	eval_summary: TraceEvalSummary | null;
}

export interface TraceEvalSummary {
	badcase_id: string | null;
	eval_run_id: string | null;
	sample_result_id: string | null;
	sample_id: string | null;
	dataset_id: string | null;
	dataset_version: string | null;
	status: string | null;
	severity: string | null;
	impact_target: string | null;
	root_cause_hint: string | null;
	responsible_asset: string | null;
	rerun_status: string | null;
	trace_id: string | null;
	admin_run_id: string | null;
	expected_intent: string | null;
	actual_intent: string | null;
	intent_match: boolean | null;
	expected_pb: string | null;
	actual_pb: string | null;
	pb_match: boolean | null;
	graduation_dataset_id: string | null;
	graduation_dataset_version: string | null;
	close_recommendation: "ready_to_close" | "needs_follow_up" | "not_ready";
	close_reason: string;
}

export function pickIntentEvent(events: TraceEvent[]): IntentRecognitionEvent | null {
	const e = events.find(
		(it): it is IntentRecognitionEvent =>
			(it as { phase?: unknown }).phase === "intent_recognition",
	);
	return e ?? null;
}

export function pickPlaybookEvent(events: TraceEvent[]): PlaybookRecommendationEvent | null {
	const e = events.find(
		(it): it is PlaybookRecommendationEvent =>
			(it as { phase?: unknown }).phase === "playbook_recommendation",
	);
	return e ?? null;
}

export function useTraceDetail(traceId: string | null) {
	return useQuery<TraceDetail>({
		queryKey: ["admin", "trace", traceId],
		queryFn: async () => {
			const { data } = await apiClient.get<TraceDetail>(`/admin/v1/trace/${traceId}`);
			return data;
		},
		enabled: traceId !== null && traceId.length >= 8,
	});
}

// ─── trace 列表/检索（W4 · 补 trace 检索命门）──────────────────────────────

export interface TraceListItem {
	trace_id: string;
	session_id: string;
	merchant_id: string;
	timestamp: string;
	turn_index: number;
	intent: string | null;
	final_action_type: string;
	final_outcome: string;
	degraded_modules: string[];
	handoff_reason: string | null;
	total_latency_ms: number;
}

export interface TraceListResponse {
	items: TraceListItem[];
	total: number;
	limit: number;
	offset: number;
}

export interface TraceListFilters {
	merchant_id?: string;
	session_id?: string;
	final_outcome?: string;
	degraded_only?: boolean;
	limit?: number;
	offset?: number;
}

export function useTraceList(filters: TraceListFilters, enabled = true) {
	return useQuery<TraceListResponse>({
		queryKey: ["admin", "traces", filters],
		queryFn: async () => {
			const params: Record<string, string | number | boolean> = {};
			if (filters.merchant_id) params.merchant_id = filters.merchant_id;
			if (filters.session_id) params.session_id = filters.session_id;
			if (filters.final_outcome) params.final_outcome = filters.final_outcome;
			if (filters.degraded_only) params.degraded_only = true;
			params.limit = filters.limit ?? 50;
			params.offset = filters.offset ?? 0;
			const { data } = await apiClient.get<TraceListResponse>("/admin/v1/trace", { params });
			return data;
		},
		enabled,
	});
}
